import type {
    YearSelectorNavigationDateConstantType,
    YearSelectorNavigationViewConstantType,
    YearSelectorSelectedDateConstantType,
    YearSelectorRangeResultConstantType,
    YearSelectorWindowStartConstantType,
    YearSelectorNavigationConstantType,
    CalendarYearSelectionConstantType,
    YearSelectorTabStopConstantType,
    YearSelectorPeriodConstantType,
    YearSelectInitialGeneratorType,
    YearSelectorYearsConstantType,
    YearSelectorPageConstantType,
    YearSelectorRangeResult,
    YearSelectorNavigation,
    CalendarYearSelection,
    CalendarYear
} from "./type";
import moment from "moment";
import {
    formatDateTime
} from "../../helpers/locale/format";
import {
    getHorizontalStep
} from "../../helpers/direction";

export const YEAR_SELECTOR_YEAR_TITLE_FORMAT: Intl.DateTimeFormatOptions = {
    year: "numeric"
};

export const YEAR_SELECTOR_TITLE_SEPARATOR = " – ";

export const YEAR_SELECTOR_LABEL_SEPARATOR = "–";

export const YEAR_SELECTOR_LEADING_YEAR_COUNT = 5;

export const YEAR_SELECTOR_YEAR_COUNT = 12;

export const YEAR_SELECTOR_COLUMN_COUNT = 3;

export const YEAR_SELECTOR_NAVIGATION_KEYS: Array<string> = [
    "ArrowRight",
    "ArrowLeft",
    "ArrowDown",
    "ArrowUp",
    "PageDown",
    "PageUp",
    "Home",
    "End"
];

export const isValidDate = (date?: Date): date is Date => {
    return date instanceof Date && !Number.isNaN(date.getTime());
};

export const dateSelect = ({
    dateRange,
    variant,
    date
}: YearSelectInitialGeneratorType): Date => {
    if(variant === "single" && isValidDate(date)) {
        return date;
    }

    if(variant === "range" && dateRange) {
        if(isValidDate(dateRange.start)) return dateRange.start;
        if(isValidDate(dateRange.end)) return dateRange.end;
    }

    return new Date();
};

export const getYearSelectorWindowStart = ({
    viewDate
}: YearSelectorWindowStartConstantType): moment.Moment => {
    return moment(viewDate).startOf("year").subtract(YEAR_SELECTOR_LEADING_YEAR_COUNT, "years");
};

export const getCalendarYearSelection = ({
    dateRange,
    variant,
    year,
    date
}: CalendarYearSelectionConstantType): CalendarYearSelection => {
    if(variant === "single") {
        return {
            isSelected: !!date && year.isSame(date, "year")
        };
    }

    if(variant !== "range" || !dateRange) {
        return {
            isSelected: false
        };
    }

    const {
        start,
        end
    } = dateRange;

    if(!start || !end || moment(start).isSame(end, "year")) {
        const selectedDate = start ?? end;

        return {
            isSelected: !!selectedDate && year.isSame(selectedDate, "year")
        };
    }

    if(year.isSame(start, "year")) {
        return {
            rangePosition: "start",
            isSelected: false
        };
    }

    if(year.isSame(end, "year")) {
        return {
            rangePosition: "end",
            isSelected: false
        };
    }

    if(year.isBetween(start, end, "year", "()")) {
        return {
            rangePosition: "middle",
            isSelected: false
        };
    }

    return {
        isSelected: false
    };
};

export const getYearSelectorYears = ({
    dateRange,
    viewDate,
    maxDate,
    minDate,
    variant,
    date
}: YearSelectorYearsConstantType): Array<CalendarYear> => {
    const windowStart = getYearSelectorWindowStart({
        viewDate
    });

    const today = moment();

    return Array.from({
        length: YEAR_SELECTOR_YEAR_COUNT
    }, (_, index) => {
        const year = windowStart.clone().add(index, "years");

        const selection = getCalendarYearSelection({
            dateRange,
            variant,
            year,
            date
        });

        return {
            isDisabled: (!!minDate && year.isBefore(minDate, "year")) || (!!maxDate && year.isAfter(maxDate, "year")),
            rangePosition: selection.rangePosition,
            isToday: year.isSame(today, "year"),
            isSelected: selection.isSelected,
            date: year
        };
    });
};

export const getYearSelectorRows = (years: Array<CalendarYear>): Array<Array<CalendarYear>> => {
    return Array.from({
        length: Math.ceil(years.length / YEAR_SELECTOR_COLUMN_COUNT)
    }, (_, rowIndex) => years.slice(rowIndex * YEAR_SELECTOR_COLUMN_COUNT, (rowIndex + 1) * YEAR_SELECTOR_COLUMN_COUNT));
};

export const getYearSelectorPeriod = ({
    activeLocale,
    separator,
    viewDate
}: YearSelectorPeriodConstantType): string => {
    const windowStart = getYearSelectorWindowStart({
        viewDate
    });

    const windowEnd = windowStart.clone().add(YEAR_SELECTOR_YEAR_COUNT - 1, "years");

    return [
        windowStart,
        windowEnd
    ].map((year) => formatDateTime({
        options: YEAR_SELECTOR_YEAR_TITLE_FORMAT,
        date: year.toDate(),
        locale: activeLocale
    })).join(separator);
};

export const getYearSelectorNavigation = ({
    viewDate,
    maxDate,
    minDate
}: YearSelectorNavigationConstantType): YearSelectorNavigation => {
    const windowStart = getYearSelectorWindowStart({
        viewDate
    });

    const windowEnd = windowStart.clone().add(YEAR_SELECTOR_YEAR_COUNT - 1, "years");

    return {
        isPreviousDisabled: !!minDate && !windowStart.isAfter(minDate, "year"),
        isNextDisabled: !!maxDate && !windowEnd.isBefore(maxDate, "year")
    };
};

export const getYearSelectorSelectedDate = ({
    year,
    date
}: YearSelectorSelectedDateConstantType): Date => {
    const selectedDate = year.clone().startOf("year");

    const currentDate = moment(date);

    if(!date || !currentDate.isValid()) {
        return selectedDate.toDate();
    }

    selectedDate.month(currentDate.month());

    return selectedDate.set({
        date: Math.min(currentDate.date(), selectedDate.daysInMonth()),
        millisecond: currentDate.millisecond(),
        minute: currentDate.minute(),
        second: currentDate.second(),
        hour: currentDate.hour()
    }).toDate();
};

export const getYearSelectorRangeResult = ({
    multipleSelectMinimumRequiredDayCount,
    multipleSelectDayLimit,
    dateRange,
    year
}: YearSelectorRangeResultConstantType): YearSelectorRangeResult => {
    const yearStart = year.clone().startOf("year");

    if(!dateRange || !dateRange.start) {
        return {
            dateRange: {
                start: yearStart.toDate(),
                end: undefined
            }
        };
    }

    if(yearStart.isSame(dateRange.start, "year")) {
        return {
            dateRange: {
                start: undefined,
                end: undefined
            }
        };
    }

    if(dateRange.end) {
        return {
            dateRange: {
                start: yearStart.toDate(),
                end: undefined
            }
        };
    }

    const yearDifference = yearStart.diff(moment(dateRange.start).startOf("year"), "years");

    if(yearDifference < 0) {
        return {
            dateRange: {
                start: yearStart.toDate(),
                end: undefined
            }
        };
    }

    if(multipleSelectDayLimit && yearDifference >= multipleSelectDayLimit) {
        return {
            errorKey: "maximum-selection-number-of-years-limit-exceeds"
        };
    }

    if(multipleSelectMinimumRequiredDayCount && yearDifference + 1 < multipleSelectMinimumRequiredDayCount) {
        return {
            errorKey: "minimum-selection-number-of-years-required-not-provided"
        };
    }

    return {
        dateRange: {
            end: yearStart.clone().endOf("year").toDate(),
            start: dateRange.start
        }
    };
};

export const getYearSelectorPageIndex = ({
    viewDate
}: YearSelectorPageConstantType): number => {
    return getYearSelectorWindowStart({
        viewDate
    }).year();
};

export const getYearSelectorTabStopIndex = ({
    activeDate,
    years
}: YearSelectorTabStopConstantType): number => {
    const isFocusable = (yearItem: CalendarYear) => !yearItem.isDisabled;

    const conditions: Array<(yearItem: CalendarYear) => boolean> = [
        (yearItem) => !!activeDate && yearItem.date.isSame(activeDate, "year"),
        (yearItem) => yearItem.isSelected || yearItem.rangePosition === "start" || yearItem.rangePosition === "end",
        (yearItem) => yearItem.isToday,
        () => true
    ];

    for(const condition of conditions) {
        const index = years.findIndex((yearItem) => isFocusable(yearItem) && condition(yearItem));

        if(index >= 0) {
            return index;
        }
    }

    return -1;
};

export const getYearSelectorNavigationDate = ({
    direction,
    viewDate,
    maxDate,
    minDate,
    year,
    key
}: YearSelectorNavigationDateConstantType): moment.Moment | undefined => {
    const windowStart = getYearSelectorWindowStart({
        viewDate
    });

    const rowOffset = (year.year() - windowStart.year()) % YEAR_SELECTOR_COLUMN_COUNT;

    const horizontalStep = getHorizontalStep({
        direction,
        key
    });

    const targets: Record<string, () => moment.Moment> = {
        End: () => year.clone().add(YEAR_SELECTOR_COLUMN_COUNT - 1 - rowOffset, "years"),
        ArrowUp: () => year.clone().subtract(YEAR_SELECTOR_COLUMN_COUNT, "years"),
        ArrowDown: () => year.clone().add(YEAR_SELECTOR_COLUMN_COUNT, "years"),
        PageUp: () => year.clone().subtract(YEAR_SELECTOR_YEAR_COUNT, "years"),
        PageDown: () => year.clone().add(YEAR_SELECTOR_YEAR_COUNT, "years"),
        ArrowRight: () => year.clone().add(horizontalStep, "years"),
        ArrowLeft: () => year.clone().add(horizontalStep, "years"),
        Home: () => year.clone().subtract(rowOffset, "years")
    };

    let target = targets[key]?.();

    if(!target) {
        return undefined;
    }

    if(minDate && target.isBefore(minDate, "year")) {
        target = moment(minDate).startOf("year");
    }

    if(maxDate && target.isAfter(maxDate, "year")) {
        target = moment(maxDate).startOf("year");
    }

    return target.isSame(year, "year") ? undefined : target;
};

export const getYearSelectorNavigationView = ({
    viewDate,
    target
}: YearSelectorNavigationViewConstantType): Date | undefined => {
    const windowStart = getYearSelectorWindowStart({
        viewDate
    });

    const pageCount = Math.floor((target.year() - windowStart.year()) / YEAR_SELECTOR_YEAR_COUNT);

    if(!pageCount) {
        return undefined;
    }

    return moment(viewDate).add(pageCount * YEAR_SELECTOR_YEAR_COUNT, "years").toDate();
};
